import { MicInputSource } from '../../input/mic';
import { MidiInputSource } from '../../input/midi';

export function HelpPage() {
  const midi = MidiInputSource.isSupported();
  const mic = MicInputSource.isSupported();
  return (
    <main className="page help">
      <header className="page-head">
        <h1>Aide</h1>
        <p className="lede">
          Dans ce navigateur : MIDI {midi ? 'disponible' : 'non disponible'}, micro {mic ? 'disponible' : 'non disponible'}.
        </p>
      </header>

      <section className="panel">
        <h2>Brancher un piano numérique en MIDI</h2>
        <ol>
          <li>Repérez la prise « USB to Host » (souvent carrée, type B) à l’arrière du piano. Un câble USB A vers B, comme pour une imprimante, suffit. Sur une tablette, un adaptateur vers USB-C est nécessaire.</li>
          <li>Allumez le piano avant d’ouvrir la page, puis choisissez « MIDI » comme source. Le navigateur demande l’autorisation : acceptez.</li>
          <li>Certains pianos récents proposent le Bluetooth MIDI : associez-le d’abord dans les réglages Bluetooth de l’ordinateur, il apparaît ensuite dans la liste.</li>
          <li>Si le piano est débranché pendant un exercice, la session se met en pause et reprend au rebranchement.</li>
        </ol>
      </section>

      <section className="panel">
        <h2>Configurer mon piano</h2>
        <p>
          Le parcours « Mon piano » (menu du haut) choisit la source selon ce que le navigateur permet, demande l’autorisation du micro sur un bouton, montre le niveau reçu et les notes
          reconnues, guide un court essai (trois notes isolées, puis une note répétée quatre fois) et propose les exercices compatibles. Refaites-le après un changement d’appareil ou de
          pièce.
        </p>
      </section>

      <section className="panel">
        <h2>Missions et récompenses</h2>
        <p>
          Trois premières missions : cinq notes justes pendant un exercice, un exercice court terminé en mode attente, un exercice débutant réussi en tempo imposé. Chaque mission
          rapporte des points d’expérience et un badge, une seule fois. Au clavier d’essai ou avec la synthèse, vous obtenez la version « découverte » (moins de points) ; sur votre piano
          (micro ou MIDI), la récompense complète. La réussite au tempo suit les critères habituels et n’existe que sur instrument. Une détection incertaine n’est jamais comptée, ni
          comme juste ni comme fausse. Les points sont enregistrés dans ce navigateur.
        </p>
      </section>

      <section className="panel">
        <h2>Utiliser le micro</h2>
        <ul>
          <li>Placez l’appareil sur le pupitre ou à moins d’un mètre du piano, dans une pièce calme. Fermez les fenêtres, coupez la télévision.</li>
          <li>Jouez les notes une par une, bien détachées. Le micro de cette version ne sépare pas deux notes jouées en même temps.</li>
          <li>Évitez un casque ou une enceinte Bluetooth pendant la détection : ils dégradent souvent la qualité du micro.</li>
          <li>Une note peut être reconnue une octave trop haut si son fondamental est faible (notes très graves, appareil loin du piano). Le retour dit alors « bonne note, mauvaise octave ».</li>
          <li>En mode tempo imposé, réglez la compensation de latence dans les réglages si vos notes sont jugées systématiquement en retard.</li>
          <li>Vérifiez la détection dans Réglages avant de commencer : la note reconnue et sa confiance s’affichent en direct.</li>
        </ul>
      </section>

      <section className="panel">
        <h2>Trois affichages</h2>
        <p>
          Dans l’écran de pratique, le bouton « Notes / Partition / Les deux » choisit l’affichage : les notes descendantes vers le clavier, la partition (clé de sol pour la
          main droite, clé de fa pour la main gauche, doigtés et noms de notes selon les réglages), ou les deux ensemble. Le curseur de la partition suit la séance et les notes se
          colorent selon vos résultats. Vous pouvez changer d’affichage pendant une séance sans rien perdre. En affichage « Partition », les consignes et le dernier retour s’affichent dans
          une bande entre la partition et le clavier, jamais sur les notes. Sur un téléphone, préférez « Partition » ou « Notes » seul : la vue combinée y devient petite.
        </p>
      </section>

      <section className="panel">
        <h2>Clavier d’essai</h2>
        <p>
          Sans piano, la rangée du milieu du clavier de l’ordinateur joue les touches blanches à partir du do central (Q S D F G H J K L M en AZERTY, A S D F G H J K L en QWERTY),
          la rangée du haut joue les touches noires. Les deux touches en bas à gauche (W et X en AZERTY, Z et X en QWERTY) descendent ou montent d’une octave. Vous pouvez aussi
          cliquer ou toucher le clavier affiché.
        </p>
      </section>

      <section className="panel">
        <h2>Navigateurs</h2>
        <div className="tbl">
          <table>
            <thead>
              <tr>
                <th>Navigateur</th>
                <th>MIDI</th>
                <th>Micro</th>
                <th>Remarques</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Chrome, Edge (Windows, macOS)</td>
                <td>Oui</td>
                <td>Oui</td>
                <td>Configuration de référence.</td>
              </tr>
              <tr>
                <td>Firefox (Windows, macOS)</td>
                <td>Oui, avec une permission à accorder</td>
                <td>Oui</td>
                <td></td>
              </tr>
              <tr>
                <td>Chrome (Android)</td>
                <td>Oui, USB (adaptateur OTG) ou Bluetooth</td>
                <td>Oui</td>
                <td>Latence et prétraitements variables selon l’appareil.</td>
              </tr>
              <tr>
                <td>Safari (macOS)</td>
                <td>Non</td>
                <td>Oui</td>
                <td>Utilisez le micro, ou Chrome / Firefox pour le MIDI.</td>
              </tr>
              <tr>
                <td>Safari (iPad, iPhone)</td>
                <td>Non</td>
                <td>Oui</td>
                <td>Le navigateur peut imposer des prétraitements audio. L’onglet doit rester au premier plan.</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="muted">La page doit être ouverte en HTTPS (ou sur localhost) pour que le micro et le MIDI soient accessibles.</p>
      </section>

      <section className="panel">
        <h2>Problèmes courants</h2>
        <ul>
          <li>
            <strong>« Accès au micro refusé »</strong> : cliquez sur l’icône de cadenas ou de caméra dans la barre d’adresse, autorisez le micro, puis rechargez la page. Sur iPhone et
            iPad : touchez « AA » dans la barre d’adresse de Safari → Réglages du site web → Micro → Autoriser (ou Réglages → Safari → Micro).
          </li>
          <li>
            <strong>Le micro se coupe sur iPhone ou iPad</strong> : le système suspend le micro quand la page passe en arrière-plan, quand l’écran se verrouille ou pendant un appel.
            Revenez sur la page : l’écoute reprend ; sinon, touchez à nouveau « Activer le micro ».
          </li>
          <li>
            <strong>Aucun piano MIDI détecté</strong> : vérifiez que le piano est allumé et le câble branché des deux côtés ; débranchez et rebranchez ; sur Windows, fermez les autres
            logiciels de musique qui pourraient monopoliser le piano.
          </li>
          <li>
            <strong>Beaucoup de détections incertaines</strong> : trop de bruit, appareil trop loin, notes jouées en même temps ou avec la pédale enfoncée. Jouez détaché, rapprochez
            l’appareil, ou passez en MIDI.
          </li>
          <li>
            <strong>Notes jugées en retard en mode tempo</strong> : augmentez la compensation de latence (Réglages), puis réessayez.
          </li>
        </ul>
      </section>
    </main>
  );
}
